import {
  faArrowDownUpAcrossLine,
  faArrowDownUpLock,
  faBan,
  faBuildingUser,
  faEye,
  faFileCircleXmark,
  faQuestion,
  faTags,
  faUser,
  faUserLock,
  faUserTie,
  faVialCircleCheck,
} from '@fortawesome/free-solid-svg-icons';
import { UserRole, type UserRoleName } from '@konsolenkost-de/acquisition_store_server_sdk_vue';
import { type MaybeRef } from 'vue';
import { useBadgeInfoObject } from '../style/useBadgeInfoObject';
import { type DisplayBadgeType } from '../style/useDisplayBadgeClass';

/**
 * Every assignable user role name, in enum order - handy for select/filter inputs.
 * Names (not the numeric values) are the currency used by `user.roles` and the
 * `user.role.*` i18n keys; `UserRole[name]` gives the numeric value when needed.
 */
export const userRoles = Object.keys(UserRole) as UserRoleName[];

export const useUserRoleInfo = (
  colorType: MaybeRef<DisplayBadgeType> = 'badge',
  role?: MaybeRef<UserRole>,
) => {
  const {
    badgeInfo,
    getBadgeClass,
    getBadgeColor,
    getBadgeIcon,
    getBadgeTitle,
    getBadgeInfo,
  } = useBadgeInfoObject<UserRole>(
    {
      i18nPrefix: 'user.role',
      colorMap: {
        [UserRole.ADMIN]: 'error',
        [UserRole.USER]: 'primary',
        [UserRole.OFFER_REVIEW]: 'secondary',
        [UserRole.HANDLING_REVIEW]: 'warning',
        [UserRole.READONLY]: 'neutral',
        [UserRole.BETA_TESTER]: 'accent',
        [UserRole.ITEM_HANDLING]: 'info',
        [UserRole.EXTERNAL_USER]: 'neutral',
        [UserRole.NONE]: 'neutral',
        [UserRole.EXTERNAL_INTEGRATION]: 'neutral',
        [UserRole.EXTERNAL_INTEGRATION_READONLY]: 'neutral',
        [UserRole.STORE_MANAGER]: 'error',
      },
      iconMap: {
        [UserRole.ADMIN]: faUserLock,
        [UserRole.USER]: faUser,
        [UserRole.OFFER_REVIEW]: faEye,
        [UserRole.HANDLING_REVIEW]: faEye,
        [UserRole.READONLY]: faFileCircleXmark,
        [UserRole.BETA_TESTER]: faVialCircleCheck,
        [UserRole.ITEM_HANDLING]: faTags,
        [UserRole.EXTERNAL_USER]: faBuildingUser,
        [UserRole.NONE]: faBan,
        [UserRole.EXTERNAL_INTEGRATION]: faArrowDownUpAcrossLine,
        [UserRole.EXTERNAL_INTEGRATION_READONLY]: faArrowDownUpLock,
        [UserRole.STORE_MANAGER]: faUserTie,
      },
      defaultColor: 'neutral',
      defaultIcon: faQuestion,
      numericKeyFixedDigits: 1,
    },
    colorType,
    role,
  );

  return {
    userRoles,
    userRoleInfo: badgeInfo,
    getUserRoleTitle: getBadgeTitle,
    getUserRoleClass: getBadgeClass,
    getUserRoleColor: getBadgeColor,
    getUserRoleIcon: getBadgeIcon,
    getUserRoleInfo: getBadgeInfo,
  };
};
